import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FeatureLifecycleStatus } from '@/models/common/FeatureLifecycleStatus';
import { SystemMessageDtoIn } from '@/models/in/SystemMessageDtoIn';
import { iconForMessage } from '@/components/SystemMessages/systemMessageIcons';
import SystemMessageCard from '@/components/SystemMessages/SystemMessageCard';
import { menuPlacesOf } from '@/components/Announcements/announcementPlaces';
import AnnouncementAnchor from '@/components/Announcements/AnnouncementAnchor';
import StateChip, { StateTone } from '@/components/ui/StateChip/StateChip';
import HoverHint from '@/components/ui/Tooltip/HoverHint';
import styles from './FeatureAnnouncementsSection.module.scss';

/*
 * Where the two steps of this feature's own bubble point (SeedAnnouncement_FeatureAnnouncements.sql),
 * both in this section so «הבא» stays inside the dialog: the heading, then where the wording is fixed.
 */
export const RELEASE_SECTION_ANCHOR = 'feature-announcements-release';
export const WORDING_ANCHOR = 'feature-announcements-wording';

/** What the dialog knows about who the feature reaches, as the admin has set it - saved or not. */
export interface AnnouncementCoverage {
  /** Funds the site rollout reaches. */
  funds: number;
  /** Whether people are also picked by e-mail, role or group, beyond funds. */
  byPerson: boolean;
}

interface FeatureAnnouncementsSectionProps {
  featureKey: string;
  /** The flag's announcements, one per audience. */
  messages: SystemMessageDtoIn[];
  /** The site status as saved - what users have now. Finalized counts as fully released. */
  savedStatus: FeatureLifecycleStatus | null;
  /** The site status picked in the dialog - what users will have once it is saved. */
  selectedStatus: FeatureLifecycleStatus;
  coverage: AnnouncementCoverage;
  onEdit: (message: SystemMessageDtoIn) => void;
  onAdd: () => void;
}

export type AnnouncementMoment = 'closed' | 'onSave' | 'sent' | 'widening' | 'retracting';

const AUDIENCE_ORDER: Record<SystemMessageDtoIn['audience'], number> = { coordinators: 0, all: 1, office: 2 };

const isOpen = (status: FeatureLifecycleStatus | null) => !!status && status !== FeatureLifecycleStatus.NotReleased;

/** Where the release stands for the announcements, given what is saved and what is picked. */
export const momentOf = (saved: FeatureLifecycleStatus | null, selected: FeatureLifecycleStatus): AnnouncementMoment => {
  if (!isOpen(selected)) return isOpen(saved) ? 'retracting' : 'closed';
  if (!isOpen(saved)) return 'onSave';
  if (saved === FeatureLifecycleStatus.InRollout && selected === FeatureLifecycleStatus.FullyReleased) return 'widening';
  return 'sent';
};

const TONE: Record<AnnouncementMoment, StateTone> = { closed: 'grey', onSave: 'amber', sent: 'green', widening: 'amber', retracting: 'grey' };

/**
 * «ההודעה למשתמשים» in a flag's dialog (HAT-1489): the announcements the feature's PR shipped, one
 * row per audience, and one sentence that says WHEN they go out and to WHOM - worked out from the
 * status and the rollout picked in this very dialog, so changing the status changes the sentence
 * before anything is saved. The wording is fixed from here («עריכה»), before it goes out.
 */
const FeatureAnnouncementsSection = ({ featureKey, messages, savedStatus, selectedStatus, coverage, onEdit, onAdd }: FeatureAnnouncementsSectionProps) => {
  const { t, i18n } = useTranslation();
  // «תצוגה» unfolds the message under its own row - not a window over the dialog.
  const [previewId, setPreviewId] = useState<number | null>(null);
  const moment = momentOf(savedStatus, selectedStatus);
  const ordered = [...messages].sort((a, b) => AUDIENCE_ORDER[a.audience] - AUDIENCE_ORDER[b.audience]);
  // A message switched off on «הודעות למשתמשים» stays in the list, marked, and goes out to nobody.
  const going = ordered.filter((message) => message.isActive);
  const k = (key: string, options?: Record<string, unknown>) => t(`featureFlags.dialog.announcements.${key}`, options);
  const formatDate = (iso: string) => new Date(iso).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'he-IL');

  /** «לרכזים של 3 פרויקטים», «לכל המשרד»... one part per audience, joined into the sentence. */
  const whoParts = (): string[] => {
    const full = selectedStatus === FeatureLifecycleStatus.FullyReleased;
    const parts: string[] = [];
    for (const message of going) {
      if (message.audience === 'office') {
        // With a bubble it points at the office's own screens, so it waits for the flag to open to the
        // reader; without one it is a heads-up, out the day the feature opens to a fund. Opened only to
        // people by name or role, it reaches just the office staff among them.
        if (full || (message.tour.length === 0 && coverage.funds > 0)) parts.push(k('who.office'));
        else parts.push(message.tour.length === 0 ? k('who.officeOnFunds') : k('who.officeRollout'));
      } else if (full) {
        parts.push(message.audience === 'coordinators' ? k('who.allCoordinators') : k('who.everyone'));
      } else if (coverage.funds > 0) {
        parts.push(k('who.rollout', { count: coverage.funds }) + (coverage.byPerson ? ` ${k('who.andPeople')}` : ''));
      } else {
        parts.push(coverage.byPerson ? k('who.peopleOnly') : k('who.nobody'));
      }
    }
    return parts;
  };

  const sentence = (): string => {
    if (ordered.length === 0) return '';
    if (going.length === 0) return k('when.allOff');
    const who = whoParts().join(k('who.join'));
    switch (moment) {
      case 'closed':
        return k('when.closed');
      case 'onSave':
        return k('when.onSave', { who });
      case 'widening':
        return k('when.widening', { who });
      case 'retracting':
        return k('when.retracting');
      default: {
        const at = going[0].publishedAt ?? going[0].createdAt;
        return at ? k('when.sent', { date: formatDate(at), who }) : k('when.sentNoDate', { who });
      }
    }
  };

  /** The surfaces one message reaches, as short words with the explanation on hover. */
  const placesOf = (message: SystemMessageDtoIn): { label: string; title: string }[] => {
    const places = [{ label: k('place.bell'), title: k('where.bell') }];
    const menu = message.audience === 'office' ? 'management' : 'fund';
    const menuPlaces = menuPlacesOf(featureKey).filter((place) => place.menu === menu);
    if (menuPlaces.length > 0) {
      places.push({ label: k('place.menu'), title: k('where.menu', { item: menuPlaces.map((place) => place.labelKeys.map((key) => `«${t(key)}»`).join(' ← ')).join(', ') }) });
    }
    if (message.tour.length > 0) places.push({ label: k('place.bubble', { count: message.tour.length }), title: k('where.bubble') });
    if (message.blocks.some((block) => block.kind === 'image' && block.src)) places.push({ label: k('place.shot'), title: k('where.shot') });
    if (message.showAsPopup) places.push({ label: k('place.popup'), title: k('where.popup') });
    return places;
  };

  return (
    <section className={styles.section} aria-labelledby="ff-announcements-title">
      <AnnouncementAnchor name={RELEASE_SECTION_ANCHOR}>
        <div className={styles.header}>
          <span id="ff-announcements-title" className="body-sm-semibold">{k('title')}</span>
          {ordered.length > 0 && (going.length > 0
            ? <StateChip tone={TONE[moment]}>{k(`state.${moment}`)}</StateChip>
            : <StateChip tone="grey">{k('state.off')}</StateChip>)}
        </div>
      </AnnouncementAnchor>

      {ordered.length === 0 ? (
        <AnnouncementAnchor name={WORDING_ANCHOR}>
          <div className={styles.none}>
            <span className="body-sm-semibold">{k('none')}</span>
            <button type="button" onClick={onAdd} className={`${styles.link} body-sm-semibold`}>
              + {k('add')}
            </button>
          </div>
        </AnnouncementAnchor>
      ) : (
        <>
          <p className={`${styles.sentence} body-sm-regular`}>{sentence()}</p>
          <div className={styles.rows}>
            {ordered.map((message, index) => (
              <div key={message.id} className={styles.row}>
                <span className={styles.icon} aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" width="16" height="16" focusable="false">
                    {iconForMessage(message)}
                  </svg>
                </span>
                <HoverHint hint={message.body ?? ''} className={`${styles.title} body-sm-semibold`}>{message.title}</HoverHint>
                <StateChip tone={message.audience === 'coordinators' ? 'amber' : 'grey'} className={styles.audience}>
                  {t(`announcements.audience.${message.audience}`)}
                </StateChip>
                {!message.isActive && <StateChip tone="grey" dot>{k('state.off')}</StateChip>}
                <span className={styles.places}>
                  {placesOf(message).map((place) => (
                    <HoverHint key={place.label} hint={place.title} className={`${styles.place} body-xs-regular`}>{place.label}</HoverHint>
                  ))}
                </span>
                {/* The first row's actions are the second step of this feature's own bubble. */}
                <AnnouncementAnchor name={index === 0 ? WORDING_ANCHOR : ''}>
                  <span className={styles.actions}>
                    <button type="button" onClick={() => onEdit(message)} className={`${styles.link} body-sm-semibold`}>{k('edit')}</button>
                    <button type="button" onClick={() => setPreviewId(previewId === message.id ? null : message.id)} className={`${styles.link} body-sm-semibold`} aria-expanded={previewId === message.id}>
                      {previewId === message.id ? k('hidePreview') : k('preview')}
                    </button>
                  </span>
                </AnnouncementAnchor>
                {previewId === message.id && (
                  <div className={styles.previewInline}>
                    <SystemMessageCard message={message} isPreview />
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

    </section>
  );
};

export default FeatureAnnouncementsSection;
